import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight, Pause, Play } from "lucide-react";
import { Phone, Laptop } from "./parts";
import { IosShare, IosSheet, IosAdd, IosHome } from "./screensIos";
import { AndroidMenu, AndroidList, AndroidDialog, AndroidHome } from "./screensAndroid";
import { DesktopIcon, DesktopPopup, DesktopApp } from "./screensDesktop";

const GUIDES = {
  ios: { label: "iPhone", steps: [
    [IosShare, "Apri il sito con Safari e tocca il pulsante Condividi (il quadrato con la freccia), in basso."],
    [IosSheet, "Scorri il menu e tocca «Aggiungi alla schermata Home»."],
    [IosAdd, "Tocca «Aggiungi» in alto a destra."],
    [IosHome, "Fatto! Trovi K Gestionale tra le tue app: aprilo con un tocco."],
  ] },
  android: { label: "Android", steps: [
    [AndroidMenu, "Apri il sito con Chrome e tocca i tre puntini ⋮ in alto a destra."],
    [AndroidList, "Tocca «Installa app» (o «Aggiungi a schermata Home»)."],
    [AndroidDialog, "Conferma toccando «Installa»."],
    [AndroidHome, "Fatto! Trovi K Gestionale tra le tue app: aprilo con un tocco."],
  ] },
  desktop: { label: "Computer", steps: [
    [DesktopIcon, "In Chrome o Edge clicca l'icona di installazione a destra nella barra degli indirizzi."],
    [DesktopPopup, "Clicca «Installa»."],
    [DesktopApp, "Fatto! L'app si apre in una sua finestra e la trovi sul desktop."],
  ] },
};

const DELAY = 3600;

// Guida animata passo-passo: mostra esattamente cosa toccare su iPhone, Android o computer.
export const InstallGuide = ({ initial = "ios" }) => {
  const [os, setOs] = useState(initial);
  const [i, setI] = useState(0);
  const [auto, setAuto] = useState(true);
  const steps = GUIDES[os].steps;
  useEffect(() => {
    if (!auto) return undefined;
    const t = setTimeout(() => setI((n) => (n + 1) % steps.length), DELAY);
    return () => clearTimeout(t);
  }, [i, auto, steps.length]);
  const go = (n) => { setAuto(false); setI((n + steps.length) % steps.length); };
  const pick = (k) => { setOs(k); setI(0); setAuto(true); };
  const [Screen, text] = steps[i];
  const Frame = os === "desktop" ? Laptop : Phone;

  return (
    <div className="space-y-4" data-testid="install-guide">
      <div className="grid grid-cols-3 gap-1 rounded-xl bg-secondary p-1" role="tablist">
        {Object.entries(GUIDES).map(([k, g]) => (
          <button key={k} type="button" role="tab" aria-selected={os === k} onClick={() => pick(k)} data-testid={`install-guide-tab-${k}`}
            className={`rounded-lg py-1.5 text-sm font-medium transition-colors ${os === k ? "bg-background shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"}`}>
            {g.label}
          </button>
        ))}
      </div>
      <div className="h-[400px] flex items-center justify-center">
        <Frame os={os}>
          <AnimatePresence mode="wait">
            <motion.div key={`${os}-${i}`} className="absolute inset-0" initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }} transition={{ duration: 0.25 }}>
              <Screen />
            </motion.div>
          </AnimatePresence>
        </Frame>
      </div>
      <div className="min-h-[3.5rem] text-center" data-testid={`install-steps-${os}`}>
        <div className="text-xs font-semibold uppercase tracking-wider text-primary" data-testid="install-guide-step">Passo {i + 1} di {steps.length}</div>
        <p className="text-sm mt-1" data-testid="install-guide-caption">{text}</p>
      </div>
      <div className="flex items-center justify-center gap-3">
        <button type="button" onClick={() => go(i - 1)} className="p-2 rounded-full hover:bg-secondary" aria-label="Passo precedente" data-testid="install-guide-prev"><ChevronLeft className="w-4 h-4" /></button>
        <div className="flex gap-1.5">
          {steps.map((_, n) => (
            <button key={n} type="button" onClick={() => go(n)} aria-label={`Passo ${n + 1}`} data-testid={`install-guide-dot-${n}`}
              className={`h-2 rounded-full transition-[width,background-color] duration-300 ${n === i ? "w-6 bg-primary" : "w-2 bg-muted-foreground/30"}`} />
          ))}
        </div>
        <button type="button" onClick={() => go(i + 1)} className="p-2 rounded-full hover:bg-secondary" aria-label="Passo successivo" data-testid="install-guide-next"><ChevronRight className="w-4 h-4" /></button>
        <button type="button" onClick={() => setAuto((a) => !a)} className="p-2 rounded-full hover:bg-secondary text-muted-foreground" aria-label={auto ? "Pausa" : "Riproduci"} data-testid="install-guide-play">
          {auto ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
        </button>
      </div>
    </div>
  );
};

export default InstallGuide;
